import { DOCUMENT } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { LoginInformationService } from '../login-information.service';
import { environment } from '../../../environments/environment';
import { isFakeLoginEnabled } from '../../shared/environment.util';
import {
  YdsButtonComponent,
  YdsCardComponent,
  YdsComboboxComponent,
  YdsDropdownComponent,
  YdsSpinnerComponent,
  type YdsComboboxOption,
  type YdsDropdownOption,
} from '../../yds-angular';

const LAST_TENANT_COOKIE_NAME = 'DEV_YF_LAST_TENANT';

@Component({
  selector: 'app-local-login',
  imports: [YdsCardComponent, YdsComboboxComponent, YdsDropdownComponent, YdsButtonComponent, YdsSpinnerComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  readonly accountOptions: readonly YdsComboboxOption[] = [
    { id: 'KBJCONFIG', label: 'KBJCONFIG' },
    { id: 'KBJPROCES', label: 'KBJPROCES' },
    { id: 'KBJCONFIG+KBJPROCES', label: 'KBJCONFIG+KBJPROCES' },
    { id: 'NONE', label: 'NONE' },
  ];

  readonly tenantOptions = signal<readonly YdsDropdownOption[]>([]);

  readonly selectedAccount = signal<string | null>('KBJCONFIG+KBJPROCES');
  readonly selectedTenant = signal<string | null>(null);
  readonly canLogin = computed(() => !!this.selectedAccount() && !!this.selectedTenant());
  readonly isLoggingIn = signal(false);
  readonly environmentName = environment.name;

  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly loginInformationService = inject(LoginInformationService);

  ngOnInit(): void {
    if (isFakeLoginEnabled()) {
      this.document.cookie = 'YF_SSID=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
    }

    this.loginInformationService.getMasterTenants().subscribe((tenants) => {
      const options = tenants.map((tenant) => ({
        id: String(tenant.code),
        label: `${tenant.name} - ${tenant.code}`,
      }));
      this.tenantOptions.set(options);

      const lastTenant = this.getCookieValue(LAST_TENANT_COOKIE_NAME);
      const defaultTenant = options.find((option) => option.id === lastTenant) ?? options[0];
      if (defaultTenant) {
        this.selectedTenant.set(defaultTenant.id);
      }
    });
  }

  onAccountChange(value: string | null): void {
    this.selectedAccount.set(value);
  }

  onTenantChange(value: string | null): void {
    this.selectedTenant.set(value);
  }

  async login(): Promise<void> {
    const account = this.selectedAccount();
    const tenant = this.selectedTenant();
    if (!account || !tenant || this.isLoggingIn()) {
      return;
    }

    this.isLoggingIn.set(true);
    try {
      const response = await firstValueFrom(
        this.loginInformationService.createFakeJwtToken(account, tenant),
      );

      const secure = this.document.location.protocol === 'https:' ? '; Secure' : '';
      this.document.cookie = `YF_SSID=${encodeURIComponent(response.token)}; path=/; SameSite=Strict${secure}`;

      const isValid = await firstValueFrom(this.loginInformationService.validate());
      if (!isValid) {
        this.document.location.assign('/unauthorized');
        return;
      }

      this.document.cookie = `${LAST_TENANT_COOKIE_NAME}=${encodeURIComponent(tenant)}; path=/; SameSite=Lax`;

      void this.router.navigate(['/']);
    } finally {
      this.isLoggingIn.set(false);
    }
  }

  private getCookieValue(name: string): string | null {
    const match = this.document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }
}
